import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Linkedin, Facebook } from 'lucide-react';

import { BRAND } from '../config/brand';
import { useSiteContent } from '../hooks/useSiteContent';
import { SEOFooterLinks } from './SEOFooterLinks';
const LOGO_WHITE = BRAND.logoWhite;

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const siteContent = useSiteContent();

  return (
    <footer data-testid="footer" className="bg-black border-t border-white/10">
      <SEOFooterLinks />
      <div className="container-custom py-14">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-0">
          {/* Brand Column — Left */}
          <div className="lg:w-[38%] lg:pr-12 space-y-5">
            <img src={LOGO_WHITE} alt="Prop Assist" className="h-24 w-auto" />
            <p className="text-white/60 text-sm leading-relaxed max-w-xs">
              {siteContent.footer_description || "Pune's Trusted Real Estate Advisory focussed on End-to-End Real Estate Purchases & Management Solutions."}
            </p>
            <div className="flex items-center gap-3">
              <a href="https://www.instagram.com/propassistpune/" target="_blank" rel="noopener noreferrer" data-testid="social-instagram"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-brand hover:border-brand transition-colors">
                <Instagram size={18} />
              </a>
              <a href="https://in.linkedin.com/company/propassistconsulting" target="_blank" rel="noopener noreferrer" data-testid="social-linkedin"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-brand hover:border-brand transition-colors">
                <Linkedin size={18} />
              </a>
              <a href="https://www.facebook.com/propassistpune/" target="_blank" rel="noopener noreferrer" data-testid="social-facebook"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-brand hover:border-brand transition-colors">
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Right Section — 3 columns grouped tightly */}
          <div className="lg:w-[62%] grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6">
            {/* Company */}
            <div>
              <h4 className="font-outfit text-white text-base font-semibold mb-4">Company</h4>
              <ul className="space-y-2.5">
                {[
                  { name: 'About', path: '/about' },
                  { name: 'Careers', path: '/careers', badge: "We're hiring" },
                  { name: 'Terms of Service', path: '/terms-of-service' },
                  { name: 'Privacy Policy', path: '/privacy-policy' },
                ].map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="text-white/50 hover:text-brand transition-colors text-sm inline-flex items-center gap-2">
                      {link.name}
                      {link.badge && <span data-testid="footer-hiring-badge" className="text-[10px] uppercase tracking-wide bg-brand/15 text-brand px-1.5 py-0.5 rounded">{link.badge}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-outfit text-white text-base font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2.5">
                {[
                  { name: 'Blog', path: '/blog' },
                  { name: 'Compare with AI', path: '/compare' },
                  { name: 'Loan Planner', path: '/emi-calculator' },
                  { name: 'Rental Yield Calculator', path: '/rental-yield-calculator' },
                  { name: 'Enquire Now', path: '/contact' },
                ].map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="text-white/50 hover:text-brand transition-colors text-sm">{link.name}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-outfit text-white text-base font-semibold mb-4">Reach Us</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-2.5">
                  <MapPin size={16} className="text-brand mt-0.5 flex-shrink-0" />
                  <a href={BRAND.googleMapsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-maps-link"
                    className="text-white/50 hover:text-brand transition-colors text-sm leading-snug">
                    {BRAND.address}
                  </a>
                </li>
                <li className="flex items-center gap-2.5">
                  <Phone size={16} className="text-brand flex-shrink-0" />
                  <a href="tel:+918459458536" className="text-white/50 hover:text-brand text-sm">+91 84594 58536</a>
                </li>
                <li className="flex items-center gap-2.5">
                  <Mail size={16} className="text-brand flex-shrink-0" />
                  <a href="mailto:sales@propassist.in" className="text-white/50 hover:text-brand text-sm">sales@propassist.in</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-5 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-white/40 text-xs">
            &copy; {currentYear} Bluerock Enterprises LLP. All rights reserved.
          </p>
          <p className="text-white/40 text-xs">
            RERA Registration: <span className="text-brand">A52100047490</span>
          </p>
        </div>
      </div>
    </footer>
  );
};
